import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../config/supabase';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUser, FiMail, FiCalendar, FiLock, FiUnlock, FiSearch, FiFilter } = FiIcons;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users_mt2024')
        .select(`
          *,
          user_subscriptions_mt2024 (
            status,
            trial_end_date,
            upgraded_at
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserAccess = async (userId, isBlocked) => {
    try {
      const { error } = await supabase
        .from('users_mt2024')
        .update({ is_blocked: !isBlocked })
        .eq('id', userId);

      if (error) throw error;
      
      setUsers(prev => 
        prev.map(user => 
          user.id === userId ? { ...user, is_blocked: !isBlocked } : user
        )
      );

      toast.success(`User ${isBlocked ? 'unblocked' : 'blocked'} successfully`);
    } catch (error) {
      console.error('Error updating user access:', error);
      toast.error('Failed to update user access');
    }
  };

  const resetPassword = async (userId, userEmail) => {
    try {
      if (window.confirm(`Send password reset email to ${userEmail}?`)) {
        const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
          redirectTo: window.location.origin + '/#/auth/reset-password',
        });

        if (error) throw error;
        toast.success('Password reset email sent successfully');
      }
    } catch (error) {
      console.error('Error sending reset email:', error);
      toast.error('Failed to send reset email');
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        // In production, you'd need to handle auth user deletion as well
        // This is just removing the user profile
        const { error } = await supabase
          .from('users_mt2024')
          .delete()
          .eq('id', userId);

        if (error) throw error;
        
        setUsers(prev => prev.filter(user => user.id !== userId));
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const getSubscriptionStatus = (user) => {
    if (!user.user_subscriptions_mt2024 || user.user_subscriptions_mt2024.length === 0) {
      return 'No subscription';
    }
    
    const subscription = user.user_subscriptions_mt2024[0];
    return subscription.status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50';
      case 'trial':
        return 'text-yellow-600 bg-yellow-50';
      case 'expired':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const status = getSubscriptionStatus(user);
    const matchesFilter = filterStatus === 'all' || status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-1">Manage and monitor user accounts</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="w-full md:w-auto flex-grow md:max-w-md relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SafeIcon icon={FiSearch} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search users by name or email"
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiFilter} className="text-gray-600" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Subscriptions</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-6 text-gray-600 font-medium">User</th>
                <th className="text-left py-3 px-6 text-gray-600 font-medium">Joined</th>
                <th className="text-left py-3 px-6 text-gray-600 font-medium">Subscription</th>
                <th className="text-left py-3 px-6 text-gray-600 font-medium">Status</th>
                <th className="text-left py-3 px-6 text-gray-600 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const status = getSubscriptionStatus(user);
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {(user.name || user.email || 'U')[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.name || 'Unnamed User'}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-600">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(status)}`}>
                        {status}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.is_blocked ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                        {user.is_blocked ? 'Blocked' : 'Active'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleUserAccess(user.id, user.is_blocked)}
                          className={`p-1 rounded-lg ${user.is_blocked ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}`}
                          title={user.is_blocked ? 'Unblock User' : 'Block User'}
                        >
                          <SafeIcon icon={user.is_blocked ? FiUnlock : FiLock} />
                        </button>
                        <button
                          onClick={() => resetPassword(user.id, user.email)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Send Password Reset"
                        >
                          <SafeIcon icon={FiMail} />
                        </button>
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete User"
                        >
                          <SafeIcon icon={FiIcons.FiTrash2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiUser} className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">Try adjusting your search or filter</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;